// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useMemo } from 'react';
import type { WizardState, WizardStepStatus } from '../types/wizard';
import type { WizardStepId } from '../types/wizard';
import { WIZARD_STEP_NAMES } from '../types/wizard';

interface UseWizardStateResult {
  state: WizardState;
  goToStep: (stepId: WizardStepId) => boolean;
  goNext: () => boolean;
  goBack: () => boolean;
  markStepComplete: (stepId: WizardStepId) => void;
  markStepError: (stepId: WizardStepId) => void;
  markStepIncomplete: (stepId: WizardStepId) => void;
  canNavigateTo: (stepId: WizardStepId) => boolean;
  areAllContentStepsComplete: () => boolean;
  getFirstStepWithErrors: () => WizardStepId | null;
}

/**
 * Wizard state hook that supports a dynamic step list.
 * `activeSteps` drives navigation, indexing, and completion checks.
 */
export function useWizardState(
  activeSteps: readonly WizardStepId[],
  initialStepId?: WizardStepId,
  initialCompletedSteps?: Set<WizardStepId>,
): UseWizardStateResult {
  const initialIndex = initialStepId ? Math.max(0, activeSteps.indexOf(initialStepId)) : 0;
  const [currentStepIndex, setCurrentStepIndex] = useState(initialIndex);
  const [stepStatuses, setStepStatuses] = useState<Map<WizardStepId, WizardStepStatus>>(() => {
    const map = new Map<WizardStepId, WizardStepStatus>();
    if (initialCompletedSteps) {
      initialCompletedSteps.forEach((id) => map.set(id, 'complete'));
    }
    return map;
  });

  // Clamp index when activeSteps shrinks
  const clampedIndex = Math.min(currentStepIndex, activeSteps.length - 1);
  if (clampedIndex !== currentStepIndex) {
    // Synchronous update during render — safe because it's a correction
    setCurrentStepIndex(clampedIndex);
  }

  const canNavigateTo = useCallback(
    (stepId: WizardStepId): boolean => {
      const targetIndex = activeSteps.indexOf(stepId);
      if (targetIndex < 0) return false;

      const idx = Math.min(currentStepIndex, activeSteps.length - 1);

      // Can always navigate to current step
      if (targetIndex === idx) return true;

      // Can navigate to previously completed steps
      if (stepStatuses.get(stepId) === 'complete') return true;

      // Can navigate to the next step if current step is complete
      const currentStepId = activeSteps[idx];
      if (
        targetIndex === idx + 1 &&
        stepStatuses.get(currentStepId) === 'complete'
      ) {
        return true;
      }

      // Can navigate to any step before the current
      if (targetIndex < idx) return true;

      return false;
    },
    [currentStepIndex, stepStatuses, activeSteps],
  );

  const goToStep = useCallback(
    (stepId: WizardStepId): boolean => {
      if (!canNavigateTo(stepId)) return false;
      const index = activeSteps.indexOf(stepId);
      if (index >= 0) {
        setCurrentStepIndex(index);
        return true;
      }
      return false;
    },
    [canNavigateTo, activeSteps],
  );

  const goNext = useCallback((): boolean => {
    const idx = Math.min(currentStepIndex, activeSteps.length - 1);
    if (idx >= activeSteps.length - 1) return false;
    setCurrentStepIndex(idx + 1);
    return true;
  }, [currentStepIndex, activeSteps]);

  const goBack = useCallback((): boolean => {
    const idx = Math.min(currentStepIndex, activeSteps.length - 1);
    if (idx <= 0) return false;
    setCurrentStepIndex(idx - 1);
    return true;
  }, [currentStepIndex, activeSteps]);

  const markStepComplete = useCallback((stepId: WizardStepId) => {
    setStepStatuses((prev) => {
      const next = new Map(prev);
      next.set(stepId, 'complete');
      return next;
    });
  }, []);

  const markStepError = useCallback((stepId: WizardStepId) => {
    setStepStatuses((prev) => {
      const next = new Map(prev);
      next.set(stepId, 'error');
      return next;
    });
  }, []);

  const markStepIncomplete = useCallback((stepId: WizardStepId) => {
    setStepStatuses((prev) => {
      const next = new Map(prev);
      next.set(stepId, 'incomplete');
      return next;
    });
  }, []);

  const areAllContentStepsComplete = useCallback((): boolean => {
    // Check all steps except Step 0 (loa-validation gate)
    for (let i = 1; i < activeSteps.length; i++) {
      if (stepStatuses.get(activeSteps[i]) !== 'complete') {
        return false;
      }
    }
    return true;
  }, [stepStatuses, activeSteps]);

  const getFirstStepWithErrors = useCallback((): WizardStepId | null => {
    for (const stepId of activeSteps) {
      if (stepStatuses.get(stepId) === 'error') {
        return stepId;
      }
    }
    return null;
  }, [stepStatuses, activeSteps]);

  const idx = Math.min(currentStepIndex, activeSteps.length - 1);

  const state = useMemo<WizardState>(
    () => ({
      currentStepId: activeSteps[idx],
      currentStepIndex: idx,
      steps: activeSteps.map((id, index) => ({
        id,
        name: WIZARD_STEP_NAMES[id],
        index,
        status: stepStatuses.get(id) || 'incomplete',
        savedAt: null,
      })),
      canNavigateForward: idx < activeSteps.length - 1,
      canNavigateBack: idx > 0,
      isFirstStep: idx === 0,
      isLastStep: idx === activeSteps.length - 1,
      allStepsComplete: areAllContentStepsComplete(),
    }),
    [idx, stepStatuses, areAllContentStepsComplete, activeSteps],
  );

  return {
    state,
    goToStep,
    goNext,
    goBack,
    markStepComplete,
    markStepError,
    markStepIncomplete,
    canNavigateTo,
    areAllContentStepsComplete,
    getFirstStepWithErrors,
  };
}
